import React, { useState } from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CustomSplitColumnCard from '@common/components/misc/CustomSplitColumnCard';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store';
import { selectOrganizationsData } from '../../../store/organization/organization-slice-selectors';
import {
  LocationsIcon,
  OrganizationsIcon,
  SupportDudeIcon,
  TransferIcon,
} from '@common/utils/icons/icons';
import TextInput from '@common/components/Inputs/TextInput';
import useServerValidationResponseEffect from '@common/hooks/useServerValidationResponseEffect';
import { resetOrganizationsRestState } from '../../../store/organization/organization-slice';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import {
  validateOrganizationAddress,
  validateOrganizationCity,
  validateOrganizationLocationLimit,
  validateOrganizationName,
  validateOrganizationPhoneNumber,
  validateOrganizationSupportPhoneNumber,
  validateOrganizationUserLimit,
  validateOrganizationZipCode,
  validateSupportEmail,
} from '../../../services/validation/organization-validation';
import useValidatedInputEffect from '@common/hooks/useValidatedInputEffect';
import CountryCode from '../../../models/enums/country-codes';
import useValidatedSelectEffect from '../../../hooks/useValidatedSelectEffect';
import OrganizationRole from '../../../models/enums/organization-roles';
import SelectInput from '../../forms/SelectInput';
import { patchOrganizationById } from '../../../store/organization/organization-actions';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import Button from '@mui/material/Button';
import {
  selectIsUserAdminOrHigher,
  selectUser,
} from '../../../store/general/general-slice-selectors';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import SearchBar from '../../ui/SearchBar';
import SearchResult from '../../../models/misc/search-result';
import SearchType from '../../../models/enums/search';
import CustomConfirmAbortDialog from '../../misc/CustomConfirmAbortDialog';
import { replaceArgsInMessage } from '../../../utils/strings';
import UserRole from '../../../models/enums/user-roles';

const GeneralOrganizationSettings: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const organizationData = useAppSelector((state) => selectOrganizationsData(state));
  const isAdmin = useAppSelector((state) => selectIsUserAdminOrHigher(state));
  const currentUser = useAppSelector((state) => selectUser(state));

  const [selectedOrganization, setSelectedOrganization] = useState<SearchResult | null>(null);

  const { resetToggle, removeServerError, getServerValidationResultOrNull } =
    useServerValidationResponseEffect({
      patchError: organizationData.rest.patchError,
      resetPatchHandler: () =>
        dispatch(
          resetOrganizationsRestState({ patchStatus: HttpFetchStatus.Idle, patchError: null }),
        ),
    });

  const organization = organizationData.current;

  const {
    value: name,
    validationResult: nameValidationResult,
    valueChangeHandler: nameChangeHandler,
  } = useValidatedInputEffect(
    organization?.name ?? '',
    (newName) => validateOrganizationName(newName, organization?.role ?? OrganizationRole.LOCATION),
    (_) => () => {},
    getServerValidationResultOrNull('name'),
    removeServerError,
    resetToggle,
  );

  const {
    value: role,
    validationResult: roleValidationResult,
    valueChangeHandler: roleChangeHandler,
  } = useValidatedSelectEffect(
    organization?.role ?? '',
    (_) => ({ isValid: true, error: null }),
    (_) => () => {},
    getServerValidationResultOrNull('role'),
    removeServerError,
    resetToggle,
  );

  const {
    value: phoneNumber,
    validationResult: phoneNumberValidationResult,
    valueChangeHandler: phoneNumberChangeHandler,
  } = useValidatedInputEffect(
    organization?.phoneNumber ?? '',
    (newValue) => validateOrganizationPhoneNumber(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('phoneNumber'),
    removeServerError,
    resetToggle,
  );

  const {
    value: address,
    validationResult: addressValidationResult,
    valueChangeHandler: addressChangeHandler,
  } = useValidatedInputEffect(
    organization?.address ?? '',
    (newValue) => validateOrganizationAddress(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('address'),
    removeServerError,
    resetToggle,
  );

  const {
    value: city,
    validationResult: cityValidationResult,
    valueChangeHandler: cityChangeHandler,
  } = useValidatedInputEffect(
    organization?.city ?? '',
    (newValue) => validateOrganizationCity(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('city'),
    removeServerError,
    resetToggle,
  );

  const {
    value: zipCode,
    validationResult: zipCodeValidationResult,
    valueChangeHandler: zipCodeChangeHandler,
  } = useValidatedInputEffect(
    organization?.zipCode ?? '',
    (newValue) => validateOrganizationZipCode(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('zipCode'),
    removeServerError,
    resetToggle,
  );

  const {
    value: erpId,
    validationResult: erpIdValidationResult,
    valueChangeHandler: erpIdChangeHandler,
  } = useValidatedInputEffect(
    '',
    (newValue) => validateOrganizationZipCode(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('erpId'),
    removeServerError,
    resetToggle,
  );

  const {
    value: country,
    validationResult: countryValidationResult,
    valueChangeHandler: countryChangeHandler,
  } = useValidatedSelectEffect(
    organization?.country ?? CountryCode.SWITZERLAND,
    (_) => ({ isValid: true, error: null }),
    (_) => () => {},
    getServerValidationResultOrNull('country'),
    removeServerError,
    resetToggle,
  );

  const {
    value: supportPhoneNumber,
    validationResult: supportPhoneNumberValidationResult,
    valueChangeHandler: supportPhoneNumberChangeHandler,
  } = useValidatedInputEffect(
    organization?.supportPhoneNumber ?? '',
    (newValue) => validateOrganizationSupportPhoneNumber(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('supportPhoneNumber'),
    removeServerError,
    resetToggle,
  );

  const {
    value: supportEmail,
    validationResult: supportEmailValidationResult,
    valueChangeHandler: supportEmailChangeHandler,
  } = useValidatedInputEffect(
    organization?.supportEmail ?? '',
    (newValue) => validateSupportEmail(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('supportEmail'),
    removeServerError,
    resetToggle,
  );

  const {
    value: userLimit,
    validationResult: userLimitValidationResult,
    valueChangeHandler: userLimitChangeHandler,
  } = useValidatedInputEffect(
    organization?.userLimit ?? 10,
    (newValue) => validateOrganizationUserLimit(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('userLimit'),
    removeServerError,
    resetToggle,
  );

  const {
    value: locationLimit,
    validationResult: locationLimitValidationResult,
    valueChangeHandler: locationLimitChangeHandler,
  } = useValidatedInputEffect(
    organization?.locationLimit ?? 10,
    (newValue) => validateOrganizationLocationLimit(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('locationLimit'),
    removeServerError,
    resetToggle,
  );

  const patchOrganizationHandler = () => {
    if (organization?.id != null) {
      dispatch(
        patchOrganizationById({
          id: organization.id,
          data: {
            name,
            phoneNumber,
            address,
            city,
            country: country as CountryCode,
            zipCode,
            role: role as OrganizationRole,
            parentId: selectedOrganization?.id ?? undefined,
            supportPhoneNumber,
            supportEmail,
            userLimit:
              currentUser.data.organization?.role === OrganizationRole.MANUFACTURER && isAdmin
                ? userLimit
                : undefined,
            locationLimit:
              currentUser.data.organization?.role === OrganizationRole.MANUFACTURER && isAdmin
                ? locationLimit
                : undefined,
          },
        }),
      );
    }
    setSelectedOrganization(null);
  };

  const formSubmitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    patchOrganizationHandler();
  };

  const closeSnackbarHandler = () => {
    dispatch(resetOrganizationsRestState({ patchStatus: HttpFetchStatus.Idle, patchError: null }));
  };

  const selectOrganizationClickHandler = (result: SearchResult) => {
    setSelectedOrganization(result);
  };

  const closeSearchHandler = () => {
    setSelectedOrganization(null);
  };

  return (
    <React.Fragment>
      <Box p={0.3}>
        <CustomSplitColumnCard
          title={t('title-general-settings')}
          content={[
            {
              left: (
                <Stack direction="row" alignItems="center" spacing={1} py={1}>
                  <OrganizationsIcon />
                  <Typography>{t('title-organization-general-settings')}</Typography>
                </Stack>
              ),
              right: (
                <form onSubmit={formSubmitHandler}>
                  <Stack py={2} spacing={2}>
                    <TextInput
                      onBlur={patchOrganizationHandler}
                      adminOnly
                      value={name}
                      onChange={nameChangeHandler}
                      validationResult={nameValidationResult}
                      label={t('input-label-organization-name')}
                    />
                    <SelectInput
                      adminOnly
                      label={t('input-label-organization-role')}
                      value={role}
                      onChange={roleChangeHandler}
                      onBlur={patchOrganizationHandler}
                      validationResult={roleValidationResult}
                    >
                      {organization?.availableRoles.map((r) => (
                        <MenuItem key={r} value={r}>
                          {t('organization-role-' + r)}
                        </MenuItem>
                      ))}
                    </SelectInput>
                    {organization?.role === OrganizationRole.CUSTOMER && (
                      <TextInput
                        onBlur={patchOrganizationHandler}
                        adminOnly
                        value={erpId}
                        onChange={erpIdChangeHandler}
                        validationResult={erpIdValidationResult}
                        label={t('input-label-organization-erp-id')}
                      />
                    )}
                    {currentUser.data.organization?.role === OrganizationRole.MANUFACTURER &&
                      isAdmin && (
                        <Stack direction="row">
                          <TextInput
                            onBlur={patchOrganizationHandler}
                            value={userLimit}
                            onChange={userLimitChangeHandler}
                            validationResult={userLimitValidationResult}
                            label={t('input-label-organization-user-limit')}
                          />
                          <TextInput
                            onBlur={patchOrganizationHandler}
                            value={locationLimit}
                            onChange={locationLimitChangeHandler}
                            validationResult={locationLimitValidationResult}
                            label={t('input-label-organization-location-limit')}
                          />
                        </Stack>
                      )}
                  </Stack>
                  <Button sx={{ display: 'none' }} type="submit" />
                </form>
              ),
            },
            {
              left: (
                <Stack direction="row" alignItems="center" spacing={1} py={1}>
                  <LocationsIcon />
                  <Typography>{t('title-organization-address-settings')}</Typography>
                </Stack>
              ),
              right: (
                <form onSubmit={formSubmitHandler}>
                  <Stack py={2} spacing={2}>
                    <TextInput
                      adminOnly
                      onBlur={patchOrganizationHandler}
                      value={phoneNumber}
                      onChange={phoneNumberChangeHandler}
                      validationResult={phoneNumberValidationResult}
                      label={t('input-label-organization-phone-number')}
                    />
                    <Stack spacing={2}>
                      <TextInput
                        onBlur={patchOrganizationHandler}
                        adminOnly
                        value={address}
                        onChange={addressChangeHandler}
                        validationResult={addressValidationResult}
                        label={t('input-label-organization-address')}
                      />
                      <TextInput
                        onBlur={patchOrganizationHandler}
                        adminOnly
                        value={city}
                        onChange={cityChangeHandler}
                        validationResult={cityValidationResult}
                        label={t('input-label-organization-city')}
                      />
                      <Stack direction="row">
                        <SelectInput
                          adminOnly
                          onBlur={patchOrganizationHandler}
                          label={t('input-label-organization-country')}
                          value={country}
                          onChange={countryChangeHandler}
                          validationResult={countryValidationResult}
                        >
                          {Object.values(CountryCode).map((c) => (
                            <MenuItem key={c} value={c}>
                              {t('country-' + c)}
                            </MenuItem>
                          ))}
                        </SelectInput>
                        <TextInput
                          onBlur={patchOrganizationHandler}
                          adminOnly
                          value={zipCode}
                          onChange={zipCodeChangeHandler}
                          validationResult={zipCodeValidationResult}
                          label={t('input-label-organization-zip-code')}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                  <Button sx={{ display: 'none' }} type="submit" />
                </form>
              ),
            },
            organization?.role !== OrganizationRole.LOCATION
              ? {
                  left: (
                    <Stack direction="row" alignItems="center" spacing={1} py={1}>
                      <SupportDudeIcon />
                      <Typography>{t('title-organization-support-settings')}</Typography>
                    </Stack>
                  ),
                  right: (
                    <form onSubmit={formSubmitHandler}>
                      <Stack py={2} spacing={2}>
                        <TextInput
                          onBlur={patchOrganizationHandler}
                          adminOnly
                          value={supportEmail}
                          onChange={supportEmailChangeHandler}
                          validationResult={supportEmailValidationResult}
                          label={t('input-label-organization-support-email')}
                        />
                        <TextInput
                          onBlur={patchOrganizationHandler}
                          adminOnly
                          value={supportPhoneNumber}
                          onChange={supportPhoneNumberChangeHandler}
                          validationResult={supportPhoneNumberValidationResult}
                          label={t('input-label-organization-support-phone-number')}
                        />
                      </Stack>
                      <Button sx={{ display: 'none' }} type="submit" />
                    </form>
                  ),
                }
              : null,
            isAdmin &&
            organization != null &&
            organization.id !== currentUser.data.organization.id &&
            !(
              organization.parent?.id === currentUser.data.organization.id &&
              currentUser.data.role === UserRole.MODERATOR
            ) &&
            ![OrganizationRole.MANUFACTURER, OrganizationRole.IMPORTER].includes(organization.role)
              ? {
                  left: (
                    <Stack spacing={2} pr={2}>
                      <Stack direction="row" alignItems="center" spacing={1} py={1}>
                        <TransferIcon />
                        <Typography>{t('title-transfer-organization')}</Typography>
                      </Stack>
                      <CustomAlert
                        severity="info"
                        header={`${t('info-label-transfer-from')} ${organization?.parent?.name ?? ''}`}
                      />
                      <Typography>{t('info-transfer-organization')}</Typography>
                    </Stack>
                  ),
                  right: (
                    <Stack py={2}>
                      {organization != null && (
                        <SearchBar
                          hideTitle
                          topLabel={t('input-label-transfer-to')}
                          onItemClick={selectOrganizationClickHandler}
                          initialValue={selectedOrganization?.name}
                          onClose={closeSearchHandler}
                          fields={[SearchType.ORGANIZATION]}
                          fullSize
                          inline
                          orgRole={organization.role}
                          resultsFilter={(result) =>
                            result.id !== currentUser.data.organization.id ||
                            currentUser.data.role !== UserRole.MODERATOR
                          }
                        />
                      )}
                    </Stack>
                  ),
                }
              : null,
          ]}
        />
      </Box>

      <CustomSnackbar
        open={organizationData.rest.patchStatus === HttpFetchStatus.Success}
        title={t('success-patching-' + organization?.role)}
        onClose={closeSnackbarHandler}
      />
      <CustomConfirmAbortDialog
        open={selectedOrganization != null}
        title={t('title-transfer-organization')}
        text={
          <Stack spacing={2}>
            <CustomAlert
              severity="info"
              header={replaceArgsInMessage(t('info-confirm-transfer-organization-popup-header'), [
                organization?.name ?? '',
                selectedOrganization?.name ?? '',
              ])}
            />
            <Typography variant="body2">{t('info-transfer-organization')}</Typography>
          </Stack>
        }
        confirmButton={t('button-confirm')}
        abortButton={t('button-cancel')}
        onAbort={closeSearchHandler}
        onConfirm={patchOrganizationHandler}
      />
    </React.Fragment>
  );
};

export default GeneralOrganizationSettings;
